import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment';
import Modal from 'react-bootstrap/lib/Modal';
import Button from '@/shared/components/Button';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import dateFormatService from '@/shared/services/DateFormatService';
import DatePicker from '@/shared/components/input/DatePicker';
import YesNoModal from '@/shared/components/YesNoModal';
import { falseFunc } from '@/shared/utils/validationHelpers';
const messages = defineMessages({
    ConfigureBankHolidays: {
        id: 'ConfigureBankHolidays',
        defaultMessage: 'ConfigureBankHolidays',
    },
    Close: {
        id: 'Close',
        defaultMessage: 'Close',
    },
    Add: {
        id: 'Add',
        defaultMessage: 'Add',
    },
    AddHoliday: {
        id: 'AddHoliday',
        defaultMessage: 'Add holiday',
    },
    Date: {
        id: 'Date',
        defaultMessage: 'Date',
    },
    Cancel: {
        id: 'Cancel',
        defaultMessage: 'Cancel',
    },
    msgConfirmDeleteTitle: {
        id: 'msgConfirmDeleteTitle',
        defaultMessage: 'Delete?',
    },
    msgConfirmDeleteHoliday: {
        id: 'msgConfirmDeleteHoliday',
        defaultMessage: 'Are you sure you want to delete bank holiday {date}?',
    },
});
const AddHolidayModal = ({ show, onClose, onSave, companyId }) => {
    const intl = useIntl();
    const [date, setDate] = useState(null);
    const [dateFocused, setDateFocused] = useState(false);
    const [saving, setSaving] = useState(false);
    const handleSave = async () => {
        if (!date)
            return;
        setSaving(true);
        try {
            const formattedDate = date.utc().format('YYYY-MM-DD');
            const response = await api.base.post('/api/companyholiday', {
                companyId,
                date: formattedDate,
            });
            onSave(response.data);
            setDate(null);
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsx(OkCancelModal, Object.assign({ title: intl.formatMessage(messages.AddHoliday), show: show, onClose: onClose, onSave: handleSave, disabled: saving || !date, disableFullScreen: true }, { children: _jsx("div", Object.assign({ className: "form-horizontal", role: "form" }, { children: _jsxs("div", Object.assign({ className: "form-group" }, { children: [_jsx("label", Object.assign({ htmlFor: "holidayDate", className: "col-sm-4 control-label" }, { children: intl.formatMessage(messages.Date) })), _jsx("div", Object.assign({ className: "col-sm-8" }, { children: _jsx(DatePicker, { id: "holidayDate", date: date, placeholder: intl.formatMessage(messages.Date), isOutsideRange: falseFunc, onDateChange: (d) => setDate(d), focused: dateFocused, onFocusChange: ({ focused }) => setDateFocused(focused) }) }))] })) })) })));
};
const CompanyHolidaysModal = ({ show, onClose, companyId }) => {
    const intl = useIntl();
    const currentYear = moment.utc().year();
    const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
    const [year, setYear] = useState(currentYear);
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState(null);
    const loadHolidays = (y) => {
        setLoading(true);
        api.base
            .get(`/api/companyholiday/${companyId}/${y}`)
            .then(({ data }) => {
            setHolidays(data);
        })
            .catch(handleHttpError(intl))
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        if (!show)
            return;
        loadHolidays(year);
    }, [show, companyId]);
    const handleYearChange = (y) => {
        setYear(y);
        loadHolidays(y);
    };
    const handleHolidayAdded = (newHolidays) => {
        setHolidays(newHolidays);
        setShowAddModal(false);
    };
    const handleDeleteConfirm = async () => {
        if (!holidayToDelete)
            return;
        try {
            await api.base.delete(`/api/companyholiday/${holidayToDelete.id}`);
            setHolidays(prev => prev.filter(h => h.id !== holidayToDelete.id));
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
        finally {
            setHolidayToDelete(null);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Modal, Object.assign({ show: show, onHide: onClose, enforceFocus: false, backdrop: "static" }, { children: [_jsx(Modal.Header, Object.assign({ closeButton: true }, { children: _jsx(Modal.Title, { children: intl.formatMessage(messages.ConfigureBankHolidays) }) })), _jsxs(Modal.Body, { children: [_jsx("select", Object.assign({ className: "margin10B", value: year, onChange: e => handleYearChange(Number(e.target.value)) }, { children: years.map(y => (_jsx("option", Object.assign({ value: y }, { children: y }), y))) })), loading ? (_jsx(LoadingAnimation, {})) : (_jsx("table", Object.assign({ className: "table table-condensed table-striped-tbody table-hover" }, { children: _jsx("tbody", { children: holidays.map(holiday => (_jsxs("tr", { children: [_jsx("td", { children: _jsx("span", { children: dateFormatService.formatDateInFull(holiday.date) }) }), _jsx("td", { children: _jsx("i", { className: "fa fa-trash-o clickable", onClick: () => setHolidayToDelete(holiday) }) })] }, holiday.id))) }) }))), _jsxs("button", Object.assign({ className: "btn btn-sm btn-inverse margin5T", onClick: () => setShowAddModal(true) }, { children: [_jsx("i", { className: "fa fa-plus" }), _jsx("span", Object.assign({ className: "margin5L" }, { children: intl.formatMessage(messages.Add) }))] }))] }), _jsx(Modal.Footer, { children: _jsx(Button, Object.assign({ onClick: onClose, name: "CompanyHolidays-Close" }, { children: intl.formatMessage(messages.Close) })) })] })), _jsx(AddHolidayModal, { show: showAddModal, onClose: () => setShowAddModal(false), onSave: handleHolidayAdded, companyId: companyId }), _jsx(YesNoModal, { show: !!holidayToDelete, title: intl.formatMessage(messages.msgConfirmDeleteTitle), message: intl.formatMessage(messages.msgConfirmDeleteHoliday, {
                    date: holidayToDelete ? dateFormatService.formatDateInFull(holidayToDelete.date) : '',
                }), onSave: handleDeleteConfirm, onNo: () => setHolidayToDelete(null) })] }));
};
export default CompanyHolidaysModal;
